/** Applicability: which filings each company owes, and why - the tab of Filings that the old
 * "readiness" cockpit (`/readiness`) became (ADR-0212, P10).
 *
 * The cockpit was the product's first home screen: KPIs, a readiness ring, a chart of months, a list
 * of what needed attention, the filings. Home, the scope tables and the Filings page now carry each of
 * those, with the percentage and the path to a filing on the Filings page itself. What the cockpit
 * alone showed is **the determination behind each filing**, and that is this page: one row per company
 * and regulation, the position the server decided (`required`, `exempt`, a parent's, not in scope, or
 * *needs figures*), what that position is waiting for, whether the regulation is law yet, the
 * assurance position as a question with its sources, and - one control away - every rule the server
 * applied, with its paragraph, its source and any dispute. The address is unchanged, so every old link
 * still lands here.
 *
 * **Every regulatory sentence comes from the API.** Nothing here names a regime, a date or a
 * threshold. A date in a regulation that is not law is shown as such (`in_force_label`), never counted
 * down to. `undetermined` is not a fifth status but a statement about *our data*, so it overrides the
 * position: "we cannot tell yet" is what the reader needs even when the status underneath says
 * "not in scope".
 *
 * **Assurance is a question with its sources, never an answer** (B-18, C-05): `level` is null while
 * the sources disagree, so nothing here could print one, and a regime with nothing sourced to say has
 * no line. An empty list is not "no assurance required".
 *
 * **A failed read is not "no filing assessed"**: that sentence tells a reader this company owes
 * nothing, and a dropped request produced it (EXP-29). It is said as a failed read with a retry.
 *
 * Who states the financials that a position waits on: an Admin (ADR-0069). The page offers the
 * Admin the way to them and tells everyone else whose act it is.
 */

import { CheckCircle, MinusCircle, Question, Scales } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useMe } from "../../api/auth";
import type { Obligation, Reason } from "../../api/queries";
import { useObligations } from "../../api/queries";
import { ReadFailed } from "../../components/Loaded";
import {
  Drawer,
  EMPTY_FILTERS,
  EmptyAction,
  EmptyState,
  FilterLine,
  GridTable,
  HowDrawer,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../../components/ui";
import type { ChipTone, FilterState, GridColumn } from "../../components/ui";
import { customerText, supportLevelWords } from "../../lib/customerWords";
import { shortDate } from "../../lib/format";
import { RequireSession } from "../../shell/RequireSession";
import { factLabel } from "../factLabels";
import { useGridSort } from "../gridSort";
import { plain } from "../readinessModel";
import { ScreenFrame } from "../ScreenFrame";
import { useScreen } from "../useScreen";
import styles from "./Applicability.module.css";

/** The four positions, as groups of the server's determinations: never a determination of their own.
 * `required` and `undetermined` are the two a customer has to act on, `parent` is somebody else's
 * filing, and `none` is the rest. */
type StageKey = "required" | "needs" | "parent" | "none";

const STAGES: ReadonlyArray<{ key: StageKey; label: string; tone: ChipTone }> = [
  { key: "required", label: "Must file", tone: "info" },
  { key: "needs", label: "Needs figures", tone: "warn" },
  { key: "parent", label: "Covered by a parent", tone: "neutral" },
  { key: "none", label: "Not owed", tone: "neutral" },
];

function stageOf(obligation: Obligation): StageKey {
  if (obligation.undetermined) return "needs";
  if (obligation.status === "required") return "required";
  if (obligation.status === "satisfied_by_parent") return "parent";
  return "none";
}

/** How each status reads. `undetermined` overrides the label: see the page's docstring. */
function positionOf(obligation: Obligation): { label: string; tone: ChipTone } {
  if (obligation.undetermined) return { label: "Needs figures", tone: "warn" };
  switch (obligation.status) {
    case "required":
      return { label: "Must file", tone: "info" };
    case "satisfied_by_parent":
      return { label: "Parent files", tone: "neutral" };
    case "exempt":
      return { label: "Exempt", tone: "neutral" };
    case "not_in_scope":
      return { label: "Not in scope", tone: "neutral" };
    default:
      return { label: obligation.status, tone: "neutral" };
  }
}

/** The filing date as the row says it. An owed filing has a date it is due; a filing that is not owed
 * may still carry one on record, and says so; a regime that sets none says that. */
function dateWords(obligation: Obligation): string {
  if (!obligation.due_date) return "No date set";
  const date = shortDate(obligation.due_date);
  return obligation.status === "required" ? `Due ${date}` : `Date on record ${date}`;
}

/** The facts an undetermined position is missing, once each and in the order the rules ask. */
function missingFacts(obligation: Obligation): string[] {
  return [
    ...new Set(obligation.reasoning.flatMap((reason) => (reason.missing ? [reason.missing] : []))),
  ];
}

/** One rule, as the customer reads it - with its paragraph, and the dispute if there is one. */
function ReasonRow({ reason }: { reason: Reason }) {
  /** The rule's result as a mark that is not only a colour: met is the filled tick, an unknown fact is
   * a question, and a rule that did not apply is a minus. The word goes to a screen reader. */
  const word = reason.missing ? "Fact missing" : reason.satisfied === "yes" ? "Met" : "Not met";
  return (
    <div className={styles.reason}>
      {reason.missing ? (
        <Question className={`${styles.mark} ${styles.markMissing}`} weight="bold" size={14} aria-hidden="true" />
      ) : reason.satisfied === "yes" ? (
        <CheckCircle className={styles.mark} weight="fill" size={14} aria-hidden="true" />
      ) : (
        <MinusCircle className={`${styles.mark} ${styles.markOff}`} weight="regular" size={14} aria-hidden="true" />
      )}
      <span className="visuallyHidden">{word}: </span>
      <span>
        {customerText(reason.statement)}
        <span className={styles.cite}>
          {reason.source_url ? (
            <a href={reason.source_url} target="_blank" rel="noreferrer">
              {customerText(reason.citation)}
            </a>
          ) : (
            customerText(reason.citation)
          )}
        </span>
        {/* The statute and the regulation disagree on how the revenue test is measured. A customer
            reading the statute reaches the opposite conclusion and says we are wrong, so the question
            is shown rather than only our answer to it. The register id the served sentence leads with
            is not (`customerText`). */}
        {reason.dispute ? <span className={styles.dispute}>{customerText(reason.dispute)}</span> : null}
      </span>
    </div>
  );
}

/** The assurance position's lead words: a level and the year it starts, or that the sources have not
 * settled it. The register id the dispute note opens with is not shown (`customerText`). */
function assuranceHead(rule: Obligation["assurance"][number]): string {
  return rule.level === null ? "Assurance level: not yet settled" : `Assurance level: ${rule.level}, from ${String(rule.first_cycle_year)}`;
}

/** One assurance rule, as a question with its sources and never an answer. */
function AssuranceLine({ rule }: { rule: Obligation["assurance"][number] }) {
  return (
    <span className={styles.assurance} data-testid="assurance">
      <strong>{assuranceHead(rule)}</strong> {customerText(rule.dispute ?? rule.description)}{" "}
      <em>{customerText(rule.citation)}</em>
    </span>
  );
}

function ApplicabilityBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";
  //: The query, so a failed read is not *"No filing has been assessed yet"* - which tells a reader
  //: this company has no filing obligation, produced by a request that failed (EXP-29).
  const determinations = useObligations();
  const obligations = determinations.data ?? [];
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const [opened, setOpened] = useState<Obligation | null>(null);

  const entities = new Map(obligations.map((o) => [o.legal_entity_id, o.legal_entity_name]));
  const stages = filter.values.position ?? [];
  const regimes = filter.values.regime ?? [];
  const needle = filter.search.trim().toLowerCase();
  const rows = obligations.filter(
    (o) =>
      (stages.length === 0 || stages.includes(stageOf(o))) &&
      (regimes.length === 0 || regimes.includes(o.regime_key)) &&
      (!needle || `${o.regime_name} ${o.legal_entity_name} ${o.jurisdiction}`.toLowerCase().includes(needle)),
  );
  const { sorted, sort, setSort } = useGridSort(rows, {
    filing: (o) => `${o.regime_name} ${o.legal_entity_name}`,
    position: (o) => positionOf(o).label,
    year: (o) => o.financial_year_end,
    due: (o) => o.due_date ?? "",
  });
  const count = (stage: StageKey) => obligations.filter((o) => stageOf(o) === stage).length;
  const regimeOptions = [...new Map(obligations.map((o) => [o.regime_key, o.regime_name])).entries()].map(
    ([value, label]) => ({ value, label: plain(label) }),
  );

  const columns: GridColumn<Obligation>[] = [
    {
      id: "filing",
      header: "Filing",
      sortable: true,
      minWidth: "220px",
      rowHeader: true,
      value: (o) => `${plain(o.regime_name)}, ${o.legal_entity_name} ${o.jurisdiction}`,
      cell: (o) => (
        <span>
          {plain(o.regime_name)}, {o.legal_entity_name}
          <span className={styles.sub}> {o.jurisdiction}</span>
        </span>
      ),
    },
    {
      id: "position",
      header: "Position",
      sortable: true,
      width: "132px",
      cell: (o) => {
        const position = positionOf(o);
        const parent = o.satisfied_by_entity_id ? entities.get(o.satisfied_by_entity_id) : undefined;
        return (
          <span>
            <StatusChip tone={position.tone} size="sm">
              {position.label}
            </StatusChip>
            {parent ? <span className={styles.sub}> Covered by {parent}</span> : null}
          </span>
        );
      },
    },
    { id: "year", header: "Year ended", sortable: true, width: "108px", cell: (o) => shortDate(o.financial_year_end) },
    {
      id: "due",
      header: "Date",
      sortable: true,
      width: "124px",
      /* **The date, and never the date alone**: three regimes give a date three meanings, so the
         words say which kind it is (a date owed, a date on record for a filing that is not owed, or
         none) and the rule's own sentence about it is in the Why drawer (`due_date_basis`). */
      cell: (o) => <span data-testid="obligation-date">{dateWords(o)}</span>,
    },
    {
      id: "law",
      header: "In force",
      width: "132px",
      /* Not a countdown, and not the whole paragraph: one regime is not yet law and another is
         enjoined, and the label says which. The full sentence is in the rules, one control away. */
      cell: (o) => (o.in_force_label ? customerText(o.in_force_label) : "In force"),
    },
    {
      id: "waiting",
      header: "Waiting for",
      width: "104px",
      /* **What this position is waiting for, named** (ADR-0069). A determination run before anything
         was stated writes one `undetermined` row per entity per regime, and "Needs figures" with no
         list is a status the reader cannot act on. */
      cell: (o) =>
        o.undetermined && missingFacts(o).length > 0 ? (
          <span data-testid="needs-facts">{missingFacts(o).map(factLabel).join(", ")}</span>
        ) : (
          "Nothing"
        ),
    },
    {
      id: "assurance",
      header: "Assurance",
      width: "200px",
      // One line, the whole sentence as its title: a paragraph of rules text made one row 473px tall.
      value: (o) =>
        o.assurance.length === 0
          ? "Nothing sourced"
          : o.assurance
              .map((rule) => `${assuranceHead(rule)} ${customerText(rule.dispute ?? rule.description)} ${customerText(rule.citation)}`)
              .join(" "),
      cell: (o) =>
        o.assurance.length === 0 ? (
          "Nothing sourced"
        ) : (
          <span className={styles.assuranceList}>
            {o.assurance.map((rule) => (
              <AssuranceLine key={rule.key} rule={rule} />
            ))}
          </span>
        ),
    },
    {
      id: "why",
      header: "Why",
      width: "92px",
      // Always a button: the drawer also carries the date's basis and the support level, which a
      // filing with no rules applied still has.
      cell: (o) => (
        <button type="button" className={styles.link} data-testid="why-open" onClick={() => setOpened(o)}>
          {o.reasoning.length === 0
            ? "No rules applied"
            : `${String(o.reasoning.length)} ${o.reasoning.length === 1 ? "check" : "checks"}`}
        </button>
      ),
    },
    {
      id: "open",
      header: "Open",
      width: "64px",
      cell: (o) => (
        <Link
          to="/reports"
          search={{
            method: screen.method,
            from: screen.window?.period_start,
            to: screen.window?.period_end,
            filing: o.obligation_id,
          }}
          className={styles.link}
          data-testid="open-filing"
          aria-label={`Open filing: ${plain(o.regime_name)}, ${o.legal_entity_name}`}
        >
          Open filing
        </Link>
      ),
    },
  ];

  const loaded = determinations.data !== undefined;

  return (
    <ScreenFrame screen={screen} title="Applicability" crumb="which filings each company owes, and why" bodyScrolls>
      <TablePage
        icon={Scales}
        title="Filings"
        label="Applicability"
        actions={
          <>
            {/* Told whose act it is. An Admin states the financials and runs determination
                (ADR-0069); everyone else is not offered a control the API would refuse. */}
            {isAdmin ? (
              <Link
                to="/financials"
                search={{ method: screen.method }}
                className={styles.link}
                data-testid="state-financials"
              >
                State financials
              </Link>
            ) : null}
            <HowDrawer infoKey="filings.applicability" label="How this is decided" />
          </>
        }
        tiles={
          loaded && obligations.length > 0 ? (
            <TileStrip label="Where the filings stand">
              {STAGES.map((stage) => (
                <Tile
                  key={stage.key}
                  value={String(count(stage.key))}
                  label={stage.label}
                  testId={`stage-${stage.key}`}
                />
              ))}
            </TileStrip>
          ) : undefined
        }
        filters={
          determinations.error || (loaded && obligations.length === 0) ? undefined : (
            <FilterLine
              filters={[
                {
                  id: "position",
                  label: "Position",
                  options: STAGES.map((stage) => ({ value: stage.key, label: stage.label })),
                },
                { id: "regime", label: "Regulation", options: regimeOptions },
              ]}
              state={filter}
              onChange={setFilter}
              resultCount={loaded ? { shown: rows.length, total: obligations.length, noun: "filings" } : undefined}
            />
          )
        }
        table={
          determinations.error ? undefined : (
            <GridTable
              caption="Filings by company and regulation"
              columns={columns}
              rows={sorted}
              rowKey={(o) => `${o.legal_entity_id}-${o.regime_key}`}
              rowAttributes={(o) => ({ "data-stage": stageOf(o) })}
              loading={determinations.isPending}
              sort={sort}
              onSortChange={setSort}
              empty={
                <EmptyState
                  compact
                  testId="applicability-empty"
                  title={
                    obligations.length > 0 ? "No filing matches the filters." : "No filing has been assessed yet."
                  }
                  icon={obligations.length > 0 ? undefined : Scales}
                  action={
                    obligations.length > 0 ? undefined : isAdmin ? (
                      <EmptyAction
                        label="State company facts"
                        allowed
                        to="/financials"
                        search={{ method: screen.method }}
                        plus={false}
                        testId="state-financials-empty"
                      />
                    ) : (
                      <span data-testid="financials-who-can-empty">
                        An Admin states them and runs the determination.
                      </span>
                    )
                  }
                />
              }
              testId="obligations"
            />
          )
        }
        state={
          determinations.error ? (
            <ReadFailed
              of="The filings"
              error={determinations.error}
              onRetry={() => void determinations.refetch()}
            />
          ) : undefined
        }
      />

      <Drawer
        open={opened !== null}
        onClose={() => setOpened(null)}
        title={opened ? `Why: ${plain(opened.regime_name)}, ${opened.legal_entity_name}` : "Why"}
        width="lg"
        testId="why-drawer"
      >
        {opened ? (
          <div data-testid="why-rules">
            <p className={styles.sub}>
              {opened.reasoning.length} {opened.reasoning.length === 1 ? "check" : "checks"},{" "}
              {opened.authority}, rules version {opened.regime_version}
            </p>
            {/* The date with its basis, and where the product's part ends: the rule's own sentence
                and the served support level, both printed as the server sent them. */}
            <p className={styles.fact} data-testid="why-date">
              <strong>{dateWords(opened)}.</strong>{" "}
              <span data-testid="why-due-basis">{customerText(opened.due_date_basis)}</span>
            </p>
            <p className={styles.fact} data-testid="why-support">
              <strong data-testid="why-support-level" title={opened.support_level}>
                {supportLevelWords(opened.support_level)}
              </strong>
              .{" "}
              <span data-testid="why-support-because">{customerText(opened.support_because)}</span>
            </p>
            {opened.reasoning.map((reason, index) => (
              <ReasonRow key={`${reason.rule}-${String(index)}`} reason={reason} />
            ))}
            {opened.in_force_caveat ? <p className={styles.dispute}>{customerText(opened.in_force_caveat)}</p> : null}
          </div>
        ) : null}
      </Drawer>
    </ScreenFrame>
  );
}

/** Wrapped, because there is no unauthenticated surface any more: every endpoint this page reads takes
 * no organisation and answers only to a session. Without the wrapper the page would render its empty
 * state while every query 401s, which looks like a workspace with no data rather than a person who is
 * not signed in. */
export function ApplicabilityScreen() {
  return (
    <RequireSession>
      <ApplicabilityBody />
    </RequireSession>
  );
}

